import './Style.css';

import type { SelectableItem } from '@physitrack/championhealth-types';

import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssessmentValues } from '../../types/AssessmentPageItem';

import { Alert } from '../../../shared/components/alert/Alert';
import ModalTrigger from '../../../shared/modal/ModalTrigger';
import { ValidationContext } from '../../validation/ValidationContext';
import { CheckboxItem } from './CheckboxItem';

type CheckboxProps = {
  assessmentValues: AssessmentValues;
  onSetValue: (payload: { id: string; value: unknown }) => void;
  questionid: string;
  questionTitle: string;
  questionTitleSuffix?: string;
  modalText?: string;
  onOpenModal?: () => void;
  width: string;
  itemLimit: string;
  options?: SelectableItem[];
  exclude?: SelectableItem[];
  itemMax?: string;
  secondaryQ?: boolean;
  skippable?: boolean;
  fadeIn?: string;
};

export function Checkbox({
  assessmentValues,
  onSetValue,
  questionid,
  questionTitle,
  questionTitleSuffix,
  modalText,
  onOpenModal,
  width,
  itemLimit,
  options: optionsFromProps,
  exclude,
  itemMax,
  secondaryQ,
  skippable,
  fadeIn,
}: CheckboxProps) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [isError, setIsError] = useState(false);
  const { registerValidation } = useContext(ValidationContext);
  const { t } = useTranslation();

  function addSelectedItemHandler(item) {
    checkPlusItem(item.add);

    if (item.add) {
      if (Number(itemMax) > 0 && selectedItems.length === parseFloat(itemMax)) {
        return;
      }

      if (
        item.id === 'none' ||
        item.id === 'none_sq' ||
        item.id === 'nothing' ||
        item.id === 'none_nd'
      ) {
        // remove other options if 'none' selected
        item.class = '';
        saveValue([item]);
        if (questionid === 'areas_to_improve') {
          onSetValue({ id: 'main_area_to_improve', value: '' });
        }
      } else {
        // remove none if it exists
        const newItems = selectedItems.concat(item);
        const items_without_none = newItems.filter(
          (kitem) =>
            kitem.id !== 'none' &&
            kitem.id !== 'none_sq' &&
            kitem.id !== 'nothing' &&
            kitem.id !== 'none_nd'
        );
        // save items list
        saveValue(items_without_none);
      }
    } else {
      saveValue(selectedItems.filter((kitem) => kitem.id !== item.id));
    }
  }

  function saveValue(newSelectedItems) {
    setSelectedItems(newSelectedItems);
    // behaviour change special rule
    if (questionid === 'areas_to_improve') {
      if (
        newSelectedItems.length === 1 &&
        (newSelectedItems[0].id !== 'none' ||
          newSelectedItems[0].id !== 'none_sq' ||
          newSelectedItems[0].id !== 'none_nd')
      ) {
        onSetValue({
          id: 'main_area_to_improve',
          value: {
            id: newSelectedItems[0].id,
            title: newSelectedItems[0].title,
          },
        });
      } else if (newSelectedItems.length > 1 || newSelectedItems.length === 0) {
        onSetValue({ id: 'main_area_to_improve', value: '' });
      }
    }
    const valuesArr = newSelectedItems.map(({ add, class: _cls, ...rest }) => rest);

    onSetValue({ id: questionid, value: valuesArr });
  }

  function checkPlusItem(add) {
    let newItemCount = selectedItems.length;
    if (add) {
      newItemCount++;
    } else {
      newItemCount--;
    }
  }

  useEffect(() => {
    if (assessmentValues[questionid]) {
      const raw = assessmentValues[questionid];
      const rawArr = Array.isArray(raw) ? raw : [raw];
      const displayValues = rawArr.map((value, index) => ({
        ...value,
        class: index < Number(itemLimit) ? '' : 'hidden',
      }));
      setSelectedItems(displayValues);
    }
  }, [assessmentValues[questionid]]);

  const [blockOpacity, setBlockOpacity] = useState(0);

  useEffect(() => {
    setTimeout(function () {
      setBlockOpacity(1);
    }, 100);
  }, []);

  useEffect(() => {
    registerValidation(() => {
      if (!skippable) {
        const valid = selectedItems.length > 0;
        setIsError(!valid);
        return valid;
      }
      return true;
    });

    if (selectedItems.length > 0) {
      setIsError(false);
    }
  }, [selectedItems]);

  /* EXCLUDE ONE ARRAY FROM THE OPTIONS */
  let optionslist = optionsFromProps;
  if (exclude) {
    optionslist = optionsFromProps.filter(
      (x) => !exclude.filter((y) => y.id === x.id && x.id !== 'other').length
    );
  }
  const options = optionslist;

  return (
    <div
      className={`question_block${width}${skippable ? '' : ' req'}`}
      style={{ opacity: fadeIn ? blockOpacity : '1' }}
    >
      <div className="answer_block_container">
        <div className="answer_block">
          <fieldset
            id={questionid}
            className="checkbox active fs-exclude"
            data-input-type="checkbox"
          >
            <legend id={`${questionid}-legend`} className="assessment_question_title">
              {isError && <span className="srOnly">{t('assessment.error.inline.info')}</span>}
              {questionTitle}
            </legend>
            {modalText && (
              <ModalTrigger
                modalText={modalText}
                onOpenModal={onOpenModal}
                question={questionid}
                questionTitle={questionTitle}
              />
            )}

            {questionTitleSuffix ? (
              <span className="titleSuffix">{questionTitleSuffix}</span>
            ) : null}

            <div className="checkbox_container">
              {options.map((option) => {
                let checked;
                {
                  selectedItems.find(
                    (element) => element.id === option.id || element.id === `${option.id}_sq`
                  )
                    ? (checked = 'checked')
                    : (checked = '');
                }
                return (
                  <CheckboxItem
                    key={`${questionid}_${option.id}`}
                    item={option}
                    onAddSelectedItem={addSelectedItemHandler}
                    checked={checked}
                    secondaryQ={secondaryQ}
                  />
                );
              })}
            </div>
            <Alert
              id={`${questionid}-error`}
              isVisible={isError}
              message={t('assessment.error.inline.info.many')}
              type="error"
              isRoleAlert
            />
          </fieldset>
        </div>
      </div>
    </div>
  );
}
